/* eslint react-hooks/exhaustive-deps: "warn" */
import { useCallback, useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingHero.module.scss';

import { type FunnelRow } from './TransferPricingHero.types';

const ROWS: FunnelRow[] = [
  { label: 'Active companies in scope', note: 'DE FR NL BE AT PL', target: 612400 },
  { label: 'Activity match', note: 'described activity, not code alone', target: 38240, cut: 574160 },
  { label: 'Turnover band', note: '€8m – €120m', target: 9640, cut: 28600 },
  { label: 'Full accounts, three years', note: 'digitised from filing', target: 2870, cut: 6770 },
  { label: 'Independence and group linkage', note: 'no holder above 25%', target: 410, cut: 2460 },
  { label: 'Loss-making rule', note: 'excluded if loss 3 of 3', target: 86, cut: 324 },
  { label: 'Functional review', note: 'rejection reason logged', target: 14, cut: 72 },
  { label: 'Accepted comparables', target: 14, total: true },
];

const STEP = 1150;
const FIRST = 950;
const HOLD = 2700;
const LOOP = FIRST + (ROWS.length - 2) * STEP + 700 + HOLD;

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');

export const TransferPricingHero = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const exhibitRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const numRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const cutRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const onPointerMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const node = rootRef.current;

    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();

    node.style.setProperty('--px', `${((event.clientX - rect.left) / rect.width) * 100}%`);
    node.style.setProperty('--py', `${((event.clientY - rect.top) / rect.height) * 100}%`);
  }, []);

  useEffect(() => {
    const exhibit = exhibitRef.current;

    if (!exhibit) {
      return undefined;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let timers: ReturnType<typeof setTimeout>[] = [];
    let raf: number | null = null;

    const clearAll = () => {
      timers.forEach(clearTimeout);
      timers = [];

      if (raf) {
        cancelAnimationFrame(raf);
      }
    };

    const at = (ms: number, fn: () => void) => {
      timers.push(setTimeout(fn, ms));
    };

    const setRowState = (index: number, run: boolean, fired: boolean) => {
      const row = rowRefs.current[index];

      if (!row) {
        return;
      }

      row.classList.toggle(styles.run, run);

      if (fired) {
        row.classList.add(styles.fired);
      }
    };

    const tween = (
      index: number,
      from: number,
      to: number,
      cutTo: number | undefined,
      dur: number,
      done?: () => void,
    ) => {
      const numEl = numRefs.current[index];
      const cutEl = cutRefs.current[index];
      let t0: number | null = null;

      const tick = (ts: number) => {
        if (t0 === null) {
          t0 = ts;
        }

        const p = Math.min((ts - t0) / dur, 1);
        const e = 1 - (1 - p) ** 3;

        if (numEl) {
          numEl.textContent = fmt(from + (to - from) * e);
        }

        if (cutEl && cutTo !== undefined) {
          cutEl.textContent = `−${fmt(cutTo * e)}`;
        }

        if (p < 1) {
          raf = requestAnimationFrame(tick);
        } else if (done) {
          done();
        }
      };

      raf = requestAnimationFrame(tick);
    };

    const reset = () => {
      ROWS.forEach((row, index) => {
        const rowEl = rowRefs.current[index];
        const numEl = numRefs.current[index];
        const cutEl = cutRefs.current[index];

        rowEl?.classList.remove(styles.run, styles.pop, styles.fired);

        if (numEl) {
          numEl.textContent = fmt(row.target);
        }

        if (cutEl && row.cut !== undefined) {
          cutEl.textContent = '−0';
        }
      });

      if (progressRef.current) {
        progressRef.current.style.width = '0%';
      }
    };

    const settle = () => {
      ROWS.forEach((row, index) => {
        const rowEl = rowRefs.current[index];
        const numEl = numRefs.current[index];
        const cutEl = cutRefs.current[index];

        rowEl?.classList.remove(styles.run, styles.pop);
        rowEl?.classList.add(styles.fired);

        if (numEl) {
          numEl.textContent = fmt(row.target);
        }

        if (cutEl && row.cut !== undefined) {
          cutEl.textContent = `−${fmt(row.cut)}`;
        }
      });

      if (progressRef.current) {
        progressRef.current.style.width = '100%';
      }
    };

    const play = () => {
      clearAll();
      reset();

      let t = 0;

      ROWS.forEach((row, index) => {
        const prev = index === 0 ? 0 : ROWS[index - 1].target;
        const dur = index === 0 ? FIRST - 200 : 700;

        at(t, () => {
          setRowState(index, true, true);

          const numEl = numRefs.current[index];

          if (index > 0 && numEl) {
            numEl.textContent = fmt(prev);
          }

          tween(index, prev, row.target, row.cut, dur, () => {
            setRowState(index, false, true);

            if (row.total) {
              const rowEl = rowRefs.current[index];

              rowEl?.classList.add(styles.pop);
              at(420, () => rowEl?.classList.remove(styles.pop));
            }
          });
        });

        t += index === 0 ? FIRST : STEP;
      });

      const p0 = performance.now();

      const bar = () => {
        const elapsed = (performance.now() - p0) / LOOP;

        if (progressRef.current) {
          progressRef.current.style.width = `${Math.min(elapsed, 1) * 100}%`;
        }

        if (elapsed < 1) {
          raf = requestAnimationFrame(bar);
        }
      };

      bar();
      at(LOOP, play);
    };

    if (reduceMotion) {
      settle();

      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      play();

      return () => clearAll();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            play();
          } else {
            clearAll();
          }
        });
      },
      { threshold: 0.25 },
    );

    observer.observe(exhibit);

    return () => {
      clearAll();
      observer.disconnect();
    };
  }, []);

  return (
    <div className={styles.root} ref={rootRef} onMouseMove={onPointerMove}>
      <Section background="#081D3D" className={styles.heroSection}>
        <div className={styles.glow} aria-hidden="true" />
        <div className={styles.cursorGlow} aria-hidden="true" />

        <div className={styles.grid}>
          <div className={styles.copy}>
            <TitleBlock
              size="page"
              align="left"
              theme="dark"
              title={t('Master Transfer Pricing with First-Party Intelligence')}
              description={t(
                'Fuel your comparability analysis and defend your tax positions using direct-from-government financial data and corporate structures across 200+ jurisdictions.',
              )}
            />

            <div className={styles.actions}>
              <Button type="white" size="xxl" href="/book-a-demo">
                {t('Book a working session')}
              </Button>

              <Button type="white-outline" size="xxl" href="/data-sources">
                {t('View data sources')}
              </Button>
            </div>
          </div>

          <div className={styles.exhibit} ref={exhibitRef}>
            <div className={styles.progress} ref={progressRef} />

            <div className={styles.exhibitHead}>
              <span className={styles.refTag}>Exhibit A</span>
              <span className={styles.titleTag}>Screening — TNMM, distribution</span>
              <span className={styles.metaTag}>TP-2026-0418</span>
            </div>

            <div className={styles.exhibitMeta}>
              <div>
                <div className={styles.metaKey}>Tested party</div>
                <div className={styles.metaValue}>
                  Aurex Vertrieb GmbH
                  <span>DE · limited-risk distributor</span>
                </div>
              </div>

              <div>
                <div className={styles.metaKey}>Method</div>
                <div className={styles.metaValue}>
                  TNMM
                  <span>PLI: EBIT / sales</span>
                </div>
              </div>

              <div>
                <div className={styles.metaKey}>Period</div>
                <div className={styles.metaValue}>
                  FY2023 – FY2025
                  <span>weighted average</span>
                </div>
              </div>
            </div>

            <div className={styles.funnel}>
              <div className={styles.funnelHead}>
                <span />
                <span>Removed</span>
                <span>Remaining</span>
              </div>

              {ROWS.map((row, index) => (
                <div
                  key={row.label}
                  className={cn(styles.row, row.total && styles.rowTotal)}
                  ref={(el) => {
                    rowRefs.current[index] = el;
                  }}
                >
                  <span className={styles.index}>{row.total ? '' : String(index + 1).padStart(2, '0')}</span>

                  <span className={styles.label}>
                    {row.label}
                    {row.note && <i>{row.note}</i>}
                  </span>

                  <span
                    className={styles.cut}
                    ref={(el) => {
                      cutRefs.current[index] = el;
                    }}
                  >
                    {row.cut !== undefined ? '−0' : ''}
                  </span>

                  <span
                    className={styles.num}
                    ref={(el) => {
                      numRefs.current[index] = el;
                    }}
                  >
                    0
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};
